import React, { useCallback, useState } from "react";
import {
  Upload,
  X,
  Image as ImageIcon,
  GripVertical,
  Star,
} from "lucide-react";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { cn } from "@/lib/utils";
import { usePropertyImageUpload } from "@/hooks/useConvexImageUpload";
import { ImageDisplay } from "./image-display";
import { toast } from "sonner";

interface ImageFile {
  file: File;
  preview: string;
  id: string;
}

interface ImageUploadProps {
  value?: Array<{
    key: string;
    filename: string;
    contentType?: string;
    size?: number;
  }>;
  onChange: (
    images: Array<{
      key: string;
      filename: string;
      contentType?: string;
      size?: number;
    }>
  ) => void;
  reffId?: string;
  maxFiles?: number;
  maxSize?: number; // in bytes
  accept?: string;
  disabled?: boolean;
  className?: string;
}

export function ImageUpload({
  value = [],
  onChange,
  maxFiles = 50,
  maxSize = 5 * 1024 * 1024, // 5MB default
  accept = "image/*",
  disabled = false,
  className,
  reffId,
}: ImageUploadProps) {
  const [pendingFiles, setPendingFiles] = useState<ImageFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedImageIndex, setDraggedImageIndex] = useState<number | null>(
    null
  );
  const {
    upload,
    isUploading,
    error: uploadError,
  } = usePropertyImageUpload(reffId);

  const createImageFile = useCallback((file: File): ImageFile => {
    return {
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substring(7),
    };
  }, []);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxSize) {
        return `file size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`;
      }

      if (!file.type.startsWith("image/")) {
        return "only image files are allowed";
      }

      return null;
    },
    [maxSize]
  );

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || disabled) return;

      if (!reffId || reffId.trim() === "") {
        toast.error("Reference ID is required before uploading images");
        return;
      }

      const fileArray = Array.from(files);
      const totalFiles = value.length + pendingFiles.length + fileArray.length;

      if (totalFiles > maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }

      const validFiles: ImageFile[] = [];
      let hasError = false;

      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          hasError = true;
          break;
        }
        validFiles.push(createImageFile(file));
      }

      if (!hasError) {
        setPendingFiles((prev) => [...prev, ...validFiles]);
        // upload to Convex R2
        uploadToConvex(validFiles);
      }
    },
    [
      value.length,
      pendingFiles.length,
      maxFiles,
      disabled,
      validateFile,
      createImageFile,
    ]
  );

  const uploadToConvex = async (files: ImageFile[]) => {
    try {
      const fileObjects = files.map((f) => f.file);
      const results = await upload(fileObjects);

      const uploadedImages = results.map((result) => ({
        key: result.key,
        filename: result.filename,
        contentType: result.contentType,
        size: result.size,
      }));

      onChange([...value, ...uploadedImages]);
      setPendingFiles((prev) => prev.filter((p) => !files.includes(p)));

      // cleanup preview URLs
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    } catch (error) {
      // remove failed uploads from pending
      setPendingFiles((prev) => prev.filter((p) => !files.includes(p)));

      // cleanup preview URLs
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    }
  };

  const removeImage = useCallback(
    (index: number) => {
      const newImages = [...value];
      newImages.splice(index, 1);
      onChange(newImages);
    },
    [value, onChange]
  );

  const removePendingFile = useCallback((id: string) => {
    setPendingFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  // drag and drop reordering functions
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", index.toString());
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (disabled) return;

      // handle file drops for new uploads
      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
        return;
      }

      // handle image reordering
      const draggedIndex = draggedImageIndex;
      const dropIndex = parseInt(
        e.currentTarget.getAttribute("data-index") || "0"
      );

      if (draggedIndex !== null && draggedIndex !== dropIndex) {
        const newImages = [...value];
        const [draggedImage] = newImages.splice(draggedIndex, 1);
        newImages.splice(dropIndex, 0, draggedImage);
        onChange(newImages);

        // show success message for reordering
        if (draggedIndex === 0 || dropIndex === 0) {
          toast.success(
            "Image order updated! First image is now the thumbnail."
          );
        } else {
          toast.success("Image order updated!");
        }
      }

      setDraggedImageIndex(null);
    },
    [disabled, draggedImageIndex, value, onChange, handleFiles]
  );

  const onFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles]
  );

  return (
    <div className={cn("space-y-4", className)}>
      {/* upload area */}
      <Card
        className={cn(
          "border-2 border-dashed transition-colors",
          isDragging && "border-primary bg-primary/5",
          disabled && "opacity-50 cursor-not-allowed"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center text-center">
            <Upload className="h-10 w-10 text-muted-foreground mb-4" />
            <div className="mb-4">
              {!reffId || reffId.trim() === "" ? (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Please enter a Reference ID first
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Images will be named using the Reference ID
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-medium">
                    drag and drop images here, or{" "}
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 h-auto underline hover:bg-blue-200 rounded-none cursor-pointer"
                      disabled={disabled}
                      onClick={() =>
                        document.getElementById("image-upload")?.click()
                      }
                    >
                      browse files
                    </Button>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    up to {maxFiles} images (converted to JPG), max{" "}
                    {Math.round(maxSize / 1024 / 1024)}
                    MB each
                  </p>
                  <p className="text-xs text-amber-600 mt-2 font-medium">
                    💡 First image will be used as thumbnail - drag to reorder!
                  </p>
                </div>
              )}
            </div>
            <input
              id="image-upload"
              type="file"
              accept={accept}
              multiple
              className="hidden"
              onChange={onFileInputChange}
              disabled={disabled || !reffId || reffId.trim() === ""}
            />
          </div>
        </CardContent>
      </Card>

      {/* error message */}
      {uploadError && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {uploadError}
        </div>
      )}

      {/* image preview grid */}
      {(value.length > 0 || pendingFiles.length > 0) && (
        <div className="space-y-4">
          {/* uploaded images with drag and drop */}
          {value.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <span>Uploaded Images</span>
                <span className="text-xs text-muted-foreground">
                  (drag to reorder, first image = thumbnail)
                </span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {value.map((image, index) => (
                  <div
                    key={`uploaded-${index}`}
                    className={cn(
                      "relative group cursor-move transition-all duration-200",
                      draggedImageIndex === index && "opacity-50 scale-95",
                      index === 0 && "ring-2 ring-amber-400 ring-offset-2"
                    )}
                    draggable={!disabled}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (
                        draggedImageIndex !== null &&
                        draggedImageIndex !== index
                      ) {
                        e.currentTarget.classList.add(
                          "ring-2",
                          "ring-blue-400",
                          "ring-offset-2"
                        );
                      }
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove(
                        "ring-2",
                        "ring-blue-400",
                        "ring-offset-2"
                      );
                    }}
                    onDrop={(e) => {
                      e.currentTarget.classList.remove(
                        "ring-2",
                        "ring-blue-400",
                        "ring-offset-2"
                      );
                      handleDrop(e);
                    }}
                    data-index={index}
                  >
                    <div className="min-h-[200px] max-h-[300px] overflow-hidden rounded-lg border bg-muted flex items-center justify-center">
                      <ImageDisplay
                        imageKey={image.key}
                        alt={image.filename}
                        className="max-w-full max-h-full"
                        objectFit="contain"
                        fallback={
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <ImageIcon className="h-8 w-8 text-muted-foreground" />
                          </div>
                        }
                      />

                      {/* thumbnail indicator for first image */}
                      {index === 0 && (
                        <div className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                          <Star className="h-3 w-3 fill-current" />
                          <span className="font-semibold">Thumbnail</span>
                        </div>
                      )}

                      {/* drag handle */}
                      {!disabled && (
                        <div className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                          <GripVertical className="h-3 w-3" />
                        </div>
                      )}
                    </div>

                    {!disabled && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}

                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {image.filename}
                    </p>

                    {/* position indicator */}
                    <div
                      className="absolute -top-1 -left-1 bg-primary text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-medium shadow-lg"
                      title={
                        index === 0
                          ? "This is the thumbnail image"
                          : `Image ${index + 1} of ${value.length}`
                      }
                    >
                      {index === 0 ? "★" : index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* pending uploads */}
          {pendingFiles.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-3">Uploading...</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {pendingFiles.map((file) => (
                  <div key={file.id} className="relative group">
                    <div className="min-h-[200px] max-h-[300px] overflow-hidden rounded-lg border bg-muted flex items-center justify-center">
                      <img
                        src={file.preview}
                        alt={file.file.name}
                        className="max-w-full max-h-full object-contain opacity-50"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        {isUploading ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        ) : (
                          <div className="text-xs text-destructive bg-destructive/10 px-2 py-1 rounded">
                            Failed
                          </div>
                        )}
                      </div>
                    </div>
                    {!disabled && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removePendingFile(file.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {file.file.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* placeholder for remaining slots */}
          {value.length + pendingFiles.length < maxFiles &&
            reffId &&
            reffId.trim() !== "" && (
              <div
                className="aspect-square border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() =>
                  !disabled && document.getElementById("image-upload")?.click()
                }
              >
                <div className="text-center">
                  <ImageIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">add image</p>
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
